import * as React from "react";
import {
    AuthenticationManager,
    IFrameCommManager,
    NotificationManager,
} from "./Managers";
import {
    ContextProvider,
    ISubWindowContent,
    MessagePopup,
    SubWindow,
} from "./Components";
import {
    IMenuSectionProps,
    INavigationTowerProps,
    ITabProps,
    IToolProps,
    SideMenu,
} from "./SideMenu";
import { IToolFrameProps, ToolFrame } from "./ToolFrame";
import { StaticRepo, WatcherComponent } from "../Utilities";
import { IBaseSubSectionProps } from "./SideMenu/SubSections";
import { UserAnalytics } from "@metricell/user-analytics";
import { ViewShareMessagePopup } from "./ViewShare";

export interface ISmartToolsRootProps {
    projectDisplayName: string;
    settings: IUserSettings;
    tabs: ITabProps[];
    urlToolParam: string;
    viewShareModuleId: number;
    viewShareUrlParam: string;
}

export interface ISmartToolsRootState {
    activeSections: IActiveSectionProps[];
    activeTool: IToolFrameProps;
    popupMessage?: string;
    selectedToolID?: number;
    selectedToolSectionID?: number;
    isStoredView: boolean;
}

export interface IUserSettings {
    lastModuleID: number;
}

export interface IActiveSectionProps {
    moduleID: number;
    sectionType: string;
    subWindow?: ISubWindowContent;
    tower?: INavigationTowerProps;
}

export class SmartToolsRoot extends React.Component<
    ISmartToolsRootProps,
    ISmartToolsRootState
> {
    public static showMessage(message: string): void {
        SmartToolsRoot._message.value = message;
    }

    constructor(props: ISmartToolsRootProps) {
        super(props);

        UserAnalytics.initialize(
            `SmartPortal`,
            USER_ANALYTICS_URL,
            USER_SESSION_START_TIMESTAMP,
        ); // #FR3
        UserAnalytics.onPageLoaded();

        window.UserAnalytics = UserAnalytics;

        this._messageIter = 0;
        this._toolframeIter = 0;

        // Initialise the comm manager.
        IFrameCommManager.initialize();

        // Initialise the notification manager.
        try {
            NotificationManager.initialise();
        } catch (e) {
            console.log(e);
        }

        /* Watch the message property and show a message when it is changed. */
        SmartToolsRoot._message.addOnSetWatcher(this._showMessage);

        // Get list of all tools.
        const toolList: IToolProps[] = [];

        for (let i = 0; i < props.tabs.length; i++) {
            // All sections of type ToolMenu must have the associated extened properties.
            const tempSections: IMenuSectionProps[] = props.tabs[
                i
            ].sections.filter(
                (s) => s.sectionType === `ToolMenu`,
            ) as IMenuSectionProps[];

            for (let u = 0; u < tempSections.length; u++) {
                const tempSubSections: IBaseSubSectionProps[] =
                    tempSections[u].subSections;

                for (let e = 0; e < tempSubSections.length; e++) {
                    const tempTools: IToolProps[] = tempSubSections[e].tools;

                    for (let a = 0; a < tempTools.length; a++) {
                        toolList.push(tempTools[a]);
                    }
                }
            }
        }

        let initialTool: IToolProps;

        // If a viewshare url was provided...
        if (props.viewShareModuleId) {
            // Find whichever tool the url corresponds to in order to set the tool name and other properties.
            initialTool = toolList.find(
                (t) => t.moduleID === props.viewShareModuleId,
            );

            // If the tool has a root tool...
            if (initialTool.rootModuleID != null) {
                // Use that as the initial tool instead.
                const rootTool: IToolProps = toolList.find(
                    (t) => t.moduleID === initialTool.rootModuleID,
                );

                // Clone the initial tool to break object refernces.
                initialTool = JSON.parse(JSON.stringify(initialTool));
                initialTool.url = rootTool.url;
            }
            // Store root url before its changed
            initialTool.rootUrl = initialTool.url;

            // Then set the url the be the more specific one provided by viewshare.
            initialTool.url = `${initialTool.url}${props.viewShareUrlParam}`;
        }
        // If a url tool parameter was provided...
        else if (props.urlToolParam) {
            // Then choose the initial tool based on that...
            initialTool = toolList.find(
                (t) => t.lookupName === props.urlToolParam,
            );
            initialTool.rootUrl = initialTool.url;
        }

        // If no tool was found, or the search was not done, then choose a tool by the settings parameter which will always be defined, provided the user can actually load a single tool.
        if (initialTool == null) {
            // Then find the one whose moduleID matches the last loaded moduleID.
            initialTool = toolList.find(
                (t) => t.moduleID === props.settings.lastModuleID,
            );
            if (initialTool !== null && initialTool !== undefined) {
                initialTool.rootUrl = initialTool?.url || initialTool?.rootUrl;
            }
        }

        // However, the initial tool could still be null if they no longer have pemission to view their last loaded tool .
        if (initialTool === null || typeof initialTool === `undefined`) {
            // Then get the first tool in the list.
            initialTool = toolList[0];
            initialTool.rootUrl = initialTool.url;
        }

        const pageURL = window.location.href;

        let parameters = ``;

        if (pageURL.includes(`?`)) {
            parameters = `?${pageURL.split(`?`)[1]}`;
        }

        // Call the switch tool function to ensure that the URL and title are properly set.
        void this._switchTool(
            initialTool.moduleID,
            initialTool.name,
            initialTool.lookupName + parameters,
        );

        this.state = {
            activeSections: [],
            activeTool: {
                displayName: initialTool.name,
                moduleID: initialTool.moduleID,
                toolLookup: initialTool.lookupName + parameters,
                toolUrl: initialTool.url + parameters,
            },
            popupMessage: null,
            isStoredView: true,
        };

        // Call the method to setup the static repository.
        void this._setStaticRepo();

        // Initialise the ErrorManage.
        AuthenticationManager.initialise();
    }

    componentDidMount(): void {
        if (
            this.state.activeTool.toolUrl.includes(`ViewShareId`) ||
            this.state.activeTool.toolUrl.includes(`/shared/`) ||
            this.state.activeTool.toolUrl.includes(`/public/`)
        ) {
            void this._checkViewInLibrary(this.state.activeTool.toolUrl);
        }
    }

    private static _message: WatcherComponent<string> = new WatcherComponent();

    private _messageIter: number;

    private _toolframeIter: number;

    // This function is used to toggle sections on and off, the comparatorinator is a function passed which will allow a toggled section to interect with other active sections.
    private readonly _toggleSection = (
        section: IActiveSectionProps,
        comparatorinator: (
            activeSections: IActiveSectionProps[],
        ) => IActiveSectionProps[],
    ): void => {
        this.setState((prevState) => {
            let tempActiveSections: IActiveSectionProps[] =
                prevState.activeSections;

            // Check if the section is already active.
            if (
                prevState.activeSections.find(
                    (s) => s.moduleID === section.moduleID,
                )
            ) {
                // If so, de-activate it.
                for (let i = 0; i < tempActiveSections.length; i++) {
                    if (tempActiveSections[i].moduleID === section.moduleID) {
                        tempActiveSections.splice(i, 1);
                        i = tempActiveSections.length;
                    }
                }
            } else {
                // Else activate the section.
                if (comparatorinator) {
                    tempActiveSections = comparatorinator(tempActiveSections);
                }
                tempActiveSections.push(section);
            }

            return {
                activeSections: tempActiveSections,
            };
        });
    };

    private readonly _updateSection = (section: IActiveSectionProps): void => {
        this.setState((prevState) => {
            const tempActiveSections: IActiveSectionProps[] =
                prevState.activeSections;

            for (let i = 0; i < tempActiveSections.length; i++) {
                if (tempActiveSections[i].moduleID === section.moduleID) {
                    tempActiveSections[i] = section;
                    i = tempActiveSections.length;
                }
            }
        });
    };

    private readonly _closeSubWindow = (moduleID: number): void => {
        this.setState((prevState) => {
            const tempActiveSections: IActiveSectionProps[] =
                prevState.activeSections;

            // Check if the section is active.
            if (prevState.activeSections.find((s) => s.moduleID === moduleID)) {
                // If so, de-activate it.
                for (let i = 0; i < tempActiveSections.length; i++) {
                    if (tempActiveSections[i].moduleID === moduleID) {
                        tempActiveSections.splice(i, 1);
                        i = tempActiveSections.length;
                    }
                }
            }

            return {
                activeSections: tempActiveSections,
            };
        });
    };

    private readonly _bindToolToFrame = (
        moduleID: number,
        displayName: string,
        toolLookup: string,
        toolUrl: string,
    ): void => {
        void this._switchTool(moduleID, displayName, toolLookup);

        IFrameCommManager.resetState();

        this.setState({
            activeTool: {
                displayName,
                moduleID,
                toolLookup,
                toolUrl,
            },
        });

        // Change the toolframe key forcing it to re-render even if the props are the same.
        this._toolframeIter += 1;
    };

    private readonly _tempOpenSection = (
        tools: IToolProps[],
        moduleID: number,
        displayName: string,
        toolLookup: string,
        toolUrl: string,
    ): void => {
        if (
            tools.findIndex(
                (t) => t.moduleID === this.state.activeTool.moduleID,
            ) === -1
        ) {
            void this._switchTool(moduleID, displayName, toolLookup);

            IFrameCommManager.resetState();

            this.setState({
                activeTool: {
                    displayName,
                    moduleID,
                    toolLookup,
                    toolUrl,
                },
            });
        }
    };

    private readonly _switchTool = async (
        moduleID: number,
        toolName: string,
        toolUrlName: string,
    ): Promise<void> => {
        try {
            await $.ajax({
                data: {
                    param: moduleID,
                },
                method: `POST`,
                url: `/SmartTools/SwitchTool`,
            });

            window.history.pushState(null, toolName, `/${toolUrlName}`);
            document.title = `${this.props.projectDisplayName} - ${toolName}`;
        } catch (e) {
            console.log(e);
        }
    };

    private readonly _showMessage = (): void => {
        // Increment the message iterations by one, forcing the popup message to re-render, even if the message passed in is the same as prior.
        this._messageIter++;

        this.setState({
            popupMessage: SmartToolsRoot._message.value,
        });
    };

    private async _setStaticRepo(): Promise<void> {
        const resultString = await $.ajax({
            method: `GET`,
            url: `Images/FallbackIcon`,
        });

        StaticRepo.fallBackToolIcon = resultString;
    }

    private _addViewToLibrary(param: string): void {
        void $.ajax({
            data: {
                extension: param,
            },
            method: `POST`,
            url: `/ViewShare/AddShared`,
        });
    }

    private async _checkViewInLibrary(param: string): Promise<void> {
        const result: string = await $.ajax({
            data: {
                toolextension: param,
            },
            method: `GET`,
            url: `/ViewShare/CheckInLibrary`,
        });

        this.setState({
            isStoredView: result === `True`,
        });
    }

    render(): React.ReactElement {
        /* Load the sub windows from the list of active sections */
        const subWindows: React.ReactNode[] = [];

        for (let i = 0; i < this.state.activeSections.length; i++) {
            // If the section has a subwindow.
            if (this.state.activeSections[i].subWindow) {
                const subwindowProps: ISubWindowContent =
                    this.state.activeSections[i].subWindow;

                const subWindow: React.ReactNode = (
                    <SubWindow
                        closeWindow={this._closeSubWindow}
                        content={subwindowProps}
                        key={subwindowProps.subWindowKey}
                    />
                );

                subWindows.push(subWindow);
            }
        }

        // Register the toolframe with the comm manager.
        return (
            <ContextProvider>
                <div className={`smarttools-root`}>
                    {!this.state.isStoredView && (
                        <ViewShareMessagePopup
                            buttonAction={this._addViewToLibrary}
                            buttonParam={this.state.activeTool.toolUrl}
                            buttonText={`Save`}
                            image={`/Images/Icon_viewshare.svg`}
                            iteration={this._messageIter}
                            key={`viewshare`}
                            message={`You can save this view to your library`}
                            timeout={10000}
                        />
                    )}
                    <MessagePopup
                        iteration={this._messageIter}
                        key={`MessagePopup`}
                        message={this.state.popupMessage}
                        timeout={5000}
                    />
                    {subWindows}
                    <SideMenu
                        activeSections={this.state.activeSections}
                        activeTool={this.state.activeTool}
                        bindToolToFrame={this._bindToolToFrame}
                        key={`SideMenu`}
                        openSection={this._tempOpenSection}
                        toggleSection={this._toggleSection}
                        updateSection={this._updateSection}
                        {...this.props}
                    />
                    <ToolFrame
                        iteration={this._toolframeIter}
                        key={`ToolFrame`}
                        {...this.state.activeTool}
                    />
                </div>
            </ContextProvider>
        );
    }
}
